import React, { useState } from 'react';

export default function ContactForm({ subject = "Generel henvendelse", noCard = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Validering
    if (!formData.name || !formData.email) {
      setStatus('error');
      setErrorMessage('Venligst udfyld både navn og e-mail.');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url, // Honeypot skal medsendes
          subject: subject
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'ok') {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Der opstod en netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center shadow-sm animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
          <i className="fa-solid fa-check text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Tak for din besked!</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 px-6 py-2.5 bg-brand-dark text-white rounded-lg font-medium hover:bg-brand-accent transition-colors duration-300"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${noCard ? '' : 'bg-white p-6 md:p-8 rounded-2xl border border-brand-sand shadow-sm'}`}>
      {/* Honeypot felt - ABSOLUT SKJULT for mennesker */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="website_url">Efterlad dette felt tomt</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-brand-dark mb-2">
            Navn <span className="text-brand-accent">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className="w-full px-0 py-3 rounded-none border-b border-t-0 border-l-0 border-r-0 border-brand-sand bg-transparent text-brand-dark focus:outline-none focus:border-brand-accent transition-all duration-300"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-brand-dark mb-2">
            E-mail <span className="text-brand-accent">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="din@email.dk"
            className="w-full px-0 py-3 rounded-none border-b border-t-0 border-l-0 border-r-0 border-brand-sand bg-transparent text-brand-dark focus:outline-none focus:border-brand-accent transition-all duration-300"
          />
        </div>
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-semibold text-brand-dark mb-2">
          Telefonnummer
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="F.eks. 20910019"
          className="w-full px-0 py-3 rounded-none border-b border-t-0 border-l-0 border-r-0 border-brand-sand bg-transparent text-brand-dark focus:outline-none focus:border-brand-accent transition-all duration-300"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-semibold text-brand-dark mb-2">
          Beskrivelse
        </label>
        <textarea
          id="message"
          name="message"
          rows="4"
          value={formData.message}
          onChange={handleChange}
          placeholder="Beskriv dit projekt eller din opgave her..."
          className="w-full px-0 py-3 rounded-none border-b border-t-0 border-l-0 border-r-0 border-brand-sand bg-transparent text-brand-dark focus:outline-none focus:border-brand-accent transition-all duration-300 resize-none"
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm flex items-center gap-3">
          <i className="fa-solid fa-circle-exclamation text-base shrink-0"></i>
          <span>{errorMessage}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full py-4 bg-brand-dark text-white font-bold rounded-xl hover:bg-brand-accent transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {status === 'submitting' ? (
          <>
            <i className="fa-solid fa-spinner animate-spin"></i>
            <span>Sender besked...</span>
          </>
        ) : (
          <>
            <i className="fa-solid fa-paper-plane"></i>
            <span>Send besked</span>
          </>
        )}
      </button>
    </form>
  );
}
